import type { DomainDef, MainQuest, AttrDef, Badge, DayInfo, WeatherInfo } from "../types";

// Constants + pure helpers ported verbatim from app.html (lines 76–198, 1473–1474,
// 1576–1599). The seed *content* arrays start empty in the store; these DEFAULT
// shapes are the level-1 fallback the HUD shows until live data loads.

export const ICON = {
  heart: "fa-solid fa-heart-pulse",
  cloud: "fa-solid fa-cloud",
  brain: "fa-solid fa-brain",
  coins: "fa-solid fa-coins",
  pen: "fa-solid fa-pen-nib",
  code: "fa-solid fa-code",
  game: "fa-solid fa-gamepad",
  flag: "fa-solid fa-flag",
  inbox: "fa-solid fa-inbox",
  brief: "fa-solid fa-briefcase",
} as const;

export const TODAY0: DayInfo = { weekday: "Thursday", date: "7 August 2026", place: "Tagaytay" };
export const WEATHER0: WeatherInfo = {
  temp: "31°",
  label: "Thunderstorms",
  fa: "fa-solid fa-cloud-bolt",
  color: "#f2c14e",
  note: "Heavy rain until evening. Outdoor dailies are free to skip.",
};

export const DOMAINS: DomainDef[] = [
  { name: "Health", icon: ICON.heart, sprite: "walk", health: 78, last: "2d ago", sub: "Vitality · body and mind, both" },
  { name: "Career", icon: ICON.cloud, sprite: "laptop", health: 92, last: "today", sub: "Career · cloud, AWS and certifications" },
  { name: "Learning", icon: ICON.brain, sprite: "read", health: 61, last: "3d ago", sub: "Knowledge · books, courses, notes" },
  { name: "Finances", icon: ICON.coins, sprite: "clipboard", health: 24, last: "9d ago", sub: "Wealth · debt snowball and savings" },
  { name: "Content", icon: ICON.pen, sprite: "typing", health: 45, last: "5d ago", sub: "Career · writing and community" },
  { name: "Side Projects", icon: ICON.code, sprite: "climb", health: 55, last: "4d ago", sub: "Career · things you're building" },
  { name: "Entertainment", icon: ICON.game, sprite: "wave", health: 70, last: "1d ago", sub: "Play · games, shows, rest that counts" },
  { name: "Relationships", icon: "fa-solid fa-user-group", sprite: "speak", health: 52, last: "6d ago", sub: "Bonds · the people who matter" },
];

// Initial main quests, reassigned by applyGoals() on the live path.
export const MAINS0: MainQuest[] = [
  { id: "m1", name: "3× AWS certified", fa: "fa-solid fa-certificate", color: "#9ed85c", note: "SAA-C03 done · SOA-C02 next, then the Pro", eta: "target 2027" },
  { id: "m2", name: "Debt-free", fa: "fa-solid fa-sack-dollar", color: "#f2c14e", note: "Snowball running, smallest balance first", eta: "target mid-2027" },
  { id: "m3", name: "Ship a community talk", fa: "fa-solid fa-microphone-lines", color: "#49b8ea", note: "Outline done, deck and dry-run left", eta: "target Q4" },
  { id: "m4", name: "A body that keeps up", fa: "fa-solid fa-heart-pulse", color: "#f2799e", note: "Five sessions a week, and real rest", eta: "ongoing" },
  { id: "m5", name: "Life OS running my day", fa: "fa-solid fa-wand-magic-sparkles", color: "#49b8ea", note: "Engine, HUD, and the habit of opening it", eta: "target this quarter" },
  { id: "m6", name: "Present for my people", fa: "fa-solid fa-hand-holding-heart", color: "#ff9166", note: "Nobody I love should hear from me only when I need something", eta: "for good" },
];

// Fresh level-1 attribute slate, fallback when no live attributes are loaded.
export const ATTRS: AttrDef[] = [
  { name: "Vitality", icon: ICON.heart, tint: "rgba(242,121,158,.14)", color: "#f2799e", level: 1, into: 0, next: 500 },
  { name: "Career", icon: ICON.brief, tint: "rgba(73,184,234,.14)", color: "#49b8ea", level: 1, into: 0, next: 500 },
  { name: "Knowledge", icon: ICON.brain, tint: "rgba(158,216,92,.14)", color: "#9ed85c", level: 1, into: 0, next: 500 },
  { name: "Wealth", icon: ICON.coins, tint: "rgba(242,193,78,.14)", color: "#f2c14e", level: 1, into: 0, next: 500 },
  { name: "Bonds", icon: "fa-solid fa-hand-holding-heart", tint: "rgba(255,145,102,.14)", color: "#ff9166", level: 1, into: 0, next: 500 },
];

export const BADGES: Badge[] = [
  { name: "Seven-day streak", fa: "fa-solid fa-fire", color: "#f2c14e", earned: true, how: "Keep any quest streak alive for seven days running.", pct: 100, at: "12 / 7 days" },
  { name: "First ship", fa: "fa-solid fa-rocket", color: "#2e6fe0", earned: true, how: "Complete your first side-project quest end to end.", pct: 100, at: "done" },
  { name: "First draft", fa: "fa-solid fa-pen-nib", color: "#49b8ea", earned: true, how: "Move a content idea all the way to draft.", pct: 100, at: "done" },
  { name: "Cert earned", fa: "fa-solid fa-certificate", color: "#f2c14e", earned: true, how: "Pass an AWS certification exam.", pct: 100, at: "SAA-C03" },
  { name: "Thirty-day streak", fa: "fa-solid fa-calendar-check", color: "#f2799e", earned: false, how: "Thirty days without breaking the chain.", pct: 40, at: "12 / 30 days" },
  { name: "Debt under 60%", fa: "fa-solid fa-sack-dollar", color: "#2e6fe0", earned: false, how: "Get the snowball below 60% of where it started.", pct: 42, at: "42% cleared" },
  { name: "Hundred quests", fa: "fa-solid fa-list-check", color: "#ff9166", earned: false, how: "Complete one hundred quests, of any size.", pct: 23, at: "23 / 100" },
  { name: "Level ten", fa: "fa-solid fa-star", color: "#ff9166", earned: false, how: "Reach character level ten.", pct: 10, at: "level 1 / 10" },
  { name: "Full board", fa: "fa-solid fa-border-all", color: "#49b8ea", earned: false, how: "Every domain warm on the same day.", pct: 55, at: "5 / 9 alive" },
  { name: "Night owl", fa: "fa-solid fa-moon", color: "#ff9166", earned: false, how: "Complete a quest after midnight.", pct: 0, at: "not yet" },
  { name: "Marathon", fa: "fa-solid fa-person-running", color: "#f2799e", earned: false, how: "Log 42 km of running in a single month.", pct: 31, at: "13 / 42 km" },
  { name: "Plan complete", fa: "fa-solid fa-trophy", color: "#f2c14e", earned: false, how: "Finish a main quest end to end.", pct: 33, at: "0 of 5 done" },
  { name: "First pin", fa: "fa-solid fa-thumbtack", color: "#ff9166", earned: true, how: "Pin your one next thing for the day.", pct: 100, at: "done" },
  { name: "Deep focus", fa: "fa-solid fa-stopwatch", color: "#49b8ea", earned: true, how: "Spend 25 unbroken minutes in the focus timer.", pct: 100, at: "31 min best" },
  { name: "Clean sweep", fa: "fa-solid fa-broom", color: "#2e6fe0", earned: false, how: "Clear every quest on the board in one day.", pct: 44, at: "4 of 9 done" },
  { name: "Step by step", fa: "fa-solid fa-shoe-prints", color: "#ff9166", earned: false, how: "Complete 50 quest steps.", pct: 18, at: "9 / 50 steps" },
  { name: "Storm proof", fa: "fa-solid fa-cloud-bolt", color: "#f2c14e", earned: false, how: "Keep a daily alive through a week of bad weather.", pct: 28, at: "2 / 7 days" },
  { name: "Early bird", fa: "fa-solid fa-sun", color: "#f2c14e", earned: false, how: "Complete a quest before 7am.", pct: 0, at: "not yet" },
  { name: "Rested", fa: "fa-solid fa-couch", color: "#f2799e", earned: false, how: "Finish an Entertainment quest without guilt.", pct: 60, at: "1 of 2 done" },
  // System/xp-rules.md has defined `backlog-slayer` all along; this list never
  // carried a gem for it, so the rule had nothing to light.
  { name: "Backlog slayer", fa: "fa-solid fa-gamepad", color: "#49b8ea", earned: false, how: "Beat five games from the backlog.", pct: 0, at: "0 / 5 beaten" },
  { name: "In the black", fa: "fa-solid fa-piggy-bank", color: "#2e6fe0", earned: false, how: "End a month with income above everything going out.", pct: 71, at: "on track" },
  { name: "Ledger keeper", fa: "fa-solid fa-receipt", color: "#49b8ea", earned: false, how: "Log every peso in and out for 30 straight days.", pct: 23, at: "7 / 30 days" },
  { name: "Interest slayer", fa: "fa-solid fa-fire-flame-curved", color: "#f2c14e", earned: false, how: "Kill the balance charging you the most.", pct: 62, at: "credit card 62%" },
  { name: "Published", fa: "fa-solid fa-paper-plane", color: "#f2799e", earned: false, how: "Ship a post to the AWS community.", pct: 0, at: "0 shipped" },
  { name: "On stage", fa: "fa-solid fa-microphone-lines", color: "#ff9166", earned: false, how: "Give the talk in front of real people.", pct: 20, at: "outline done" },
  { name: "Architect", fa: "fa-solid fa-compass-drafting", color: "#f2c14e", earned: false, how: "Reach level 9 and earn the Cloud Architect title.", pct: 11, at: "level 1 / 9" },
  { name: "Vault synced", fa: "fa-solid fa-cloud-arrow-up", color: "#2e6fe0", earned: true, how: "Push the vault to S3 with nothing pending.", pct: 100, at: "in sync" },
  { name: "Ten-K day", fa: "fa-solid fa-person-walking", color: "#2e6fe0", earned: false, how: "Ten thousand steps in a single day.", pct: 47, at: "4,700 today" },
  { name: "Bookworm", fa: "fa-solid fa-book-open", color: "#49b8ea", earned: false, how: "Finish a book one chapter-quest at a time.", pct: 35, at: "ch. 7 of 20" },
  { name: "Shipper", fa: "fa-solid fa-rocket", color: "#ff9166", earned: false, how: "Ship three side projects to production.", pct: 33, at: "1 of 3" },
  { name: "Never distant", fa: "fa-solid fa-hand-holding-heart", color: "#f2799e", earned: false, how: "Nobody you love goes a month without hearing from you.", pct: 60, at: "Lola at 31 days" },
  { name: "Showed up", fa: "fa-solid fa-user-group", color: "#ff9166", earned: true, how: "Be there in person when it counted.", pct: 100, at: "done" },
  { name: "Unbroken", fa: "fa-solid fa-link", color: "#f2799e", earned: false, how: "One hundred days without breaking the chain.", pct: 12, at: "12 / 100 days" },
];

// Finance / relationship / mood / vault seed metadata (used by later phases).
export const ACC_META: Record<string, { fa: string; color: string; tint: string }> = {
  bank: { fa: "fa-solid fa-building-columns", color: "#49b8ea", tint: "rgba(73,184,234,.14)" },
  ewallet: { fa: "fa-solid fa-wallet", color: "#ff9166", tint: "rgba(255,145,102,.14)" },
  cash: { fa: "fa-solid fa-money-bill-wave", color: "#2e6fe0", tint: "rgba(46,111,224,.14)" },
};
export const EXP_CATS = [
  { key: "food", label: "Food", fa: "fa-solid fa-utensils", color: "#f2799e" },
  { key: "transport", label: "Transport", fa: "fa-solid fa-bus", color: "#49b8ea" },
  { key: "bills", label: "Bills", fa: "fa-solid fa-file-invoice", color: "#f2c14e" },
  { key: "home", label: "Home", fa: "fa-solid fa-house", color: "#ff9166" },
  // Support wears Bonds Coral: money given to family or people who depend on
  // Arlou is relationship support, not uncategorized spending.
  { key: "support", label: "Support", fa: "fa-solid fa-hand-holding-heart", color: "#ff9166" },
  { key: "fun", label: "Fun", fa: "fa-solid fa-masks-theater", color: "#2e6fe0" },
  // Travel covers the trip and the bed on it: hotels, fares, baggage. Distinct
  // from Transport, which is the daily commute (parking, Grab, fuel), because a
  // conference in Davao and the drive to the office answer different questions
  // about a month.
  //
  // Knowledge Lime, the one attribute hue no category had claimed. Every colour
  // in this list is an existing identity colour rather than a new one: Food
  // wears Vitality Rose, Transport wears Career Blue, Bills wears Wealth Gold,
  // Home wears Bonds Coral. A seventh invented hex would have been drift, and
  // the palette has no spare beyond the reserved jade, gold and damage-red.
  { key: "travel", label: "Travel", fa: "fa-solid fa-suitcase-rolling", color: "#9ed85c" },
  // Medical wears Vitality Rose: health spending is the Vitality domain. Food
  // shares the hue for the same reason, and sharing beats inventing - the
  // identity palette is closed (DESIGN.md), so a new hex would be drift. The
  // icon and label carry the difference wherever the two sit side by side.
  { key: "medical", label: "Medical", fa: "fa-solid fa-kit-medical", color: "#f2799e" },
  // Games wear Save Jade Deep with Fun: sibling domains, split so a Steam
  // purchase is not filed with movie nights. The gamepad moves here from Fun,
  // where it was generic - Fun takes the theater masks, Games take the pad.
  { key: "game", label: "Games", fa: "fa-solid fa-gamepad", color: "#2e6fe0" },
  // Keep "other" last: it is the fallback for anything unmatched, and the list
  // order is the order the chips render in.
  { key: "other", label: "Other", fa: "fa-solid fa-ellipsis", color: "#6c85b8" },
];
// Money, always to the centavo. toLocaleString's default is up to 3 fraction
// digits and no minimum, which rendered a computed interest figure as
// "₱35,695.744" and a balance as "₱247,345.7" in the same column: three decimals
// on one line, one on the next, and a third of a centavo implied on a currency
// whose smallest unit is the centavo. Two digits, always, so a column of amounts
// lines up on the decimal point.
export const peso = (n: number): string =>
  "₱" + n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const warmthColor = (r: number): string => (r >= 0.66 ? "#6ea8ff" : r >= 0.34 ? "#f2c14e" : r > 0 ? "#c9931a" : "#6c85b8");
// null is "never logged", which reads nothing like "together today". Keeping
// the two apart is the whole point of the days field being nullable.
export const sinceText = (d: number | null): string =>
  d === null ? "not logged yet" : d === 0 ? "together today" : d === 1 ? "yesterday" : d + " days ago";
export const MOODS = [
  { key: "rough", label: "Rough", fa: "fa-solid fa-cloud-rain", color: "#6c85b8" },
  { key: "low", label: "Low", fa: "fa-solid fa-cloud", color: "#49b8ea" },
  { key: "ok", label: "OK", fa: "fa-solid fa-cloud-sun", color: "#f2c14e" },
  { key: "good", label: "Good", fa: "fa-solid fa-sun", color: "#2e6fe0" },
  { key: "great", label: "Great", fa: "fa-solid fa-fire", color: "#f2799e" },
];

export const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
export const VAULT_ROOT = "~/Development/Life OS";
export const FOLDER_ORDER: [string, string][] = [
  ["Atlas", "fa-solid fa-map"], ["Calendar", "fa-solid fa-calendar-days"], ["Efforts", "fa-solid fa-flag"],
  ["Sources", "fa-solid fa-book"], ["Quests", "fa-solid fa-list-check"], ["Trackers", "fa-solid fa-chart-line"],
  ["System", "fa-solid fa-gear"], ["+Inbox", "fa-solid fa-inbox"],
];

// Title ladder + the real XP curve (kept in code, never in prompts).
export const TITLES: Record<number, string> = {
  1: "The Free-Tier Explorer", 2: "The Console Wanderer", 3: "The Script Apprentice",
  4: "The Terraform Hand", 5: "The Pipeline Builder", 6: "The Serverless Adept", 7: "The Region Runner",
  8: "The Well-Architected", 9: "The Cloud Architect", 12: "The Principal of the Realm",
};
export const TITLE_LVLS = Object.keys(TITLES).map(Number).sort((a, b) => a - b);
export function titleFor(level: number): string {
  let t = TITLES[1] as string;
  for (const l of TITLE_LVLS) if (level >= l) t = TITLES[l] as string;
  return t;
}
export const xpNeeded = (n: number): number => 500 + (n - 1) * 250;

// XP split for a quest step: the last step carries the remainder so the parts sum
// exactly to q.xp (ported from app.html stepShare).
// `active` is the list of step indices taking part this month (lib/cadence.ts
// activeSteps); a step outside its month earns nothing until it comes due.
export function stepShare(q: { xp: number }, active: number[], i: number): number {
  const n = active.length;
  const pos = active.indexOf(i);
  if (pos === -1) return 0;
  return pos === n - 1 ? q.xp - Math.floor(q.xp / n) * (n - 1) : Math.floor(q.xp / n);
}

// Health/heat scale in the records language: mint (alive) → amber (slipping) →
// coral (at risk) → muted (cold). Mirrors the navy+mint tokens.
// Steps match the HUD legend exactly: Alive / Slipping / At Risk / Cold.
export const heatColor = (h: number): string => (h >= 70 ? "#6ea8ff" : h >= 40 ? "#f2c14e" : h >= 20 ? "#ff6580" : "#6c85b8");
export const gemFill = (c: string): string =>
  "linear-gradient(135deg, color-mix(in srgb, " + c + " 82%, #fff) 0%, " + c + " 46%, color-mix(in srgb, " + c + " 62%, #000) 100%)";

// Attribute name/domain maps for applyCharacter/applyQuests.
export const ATTR_CAP: Record<string, string> = { vitality: "Vitality", career: "Career", knowledge: "Knowledge", wealth: "Wealth", bonds: "Bonds" };
export const ATTR_DOMAIN: Record<string, string> = { vitality: "Health", career: "Career", knowledge: "Learning", wealth: "Finances", bonds: "Relationships" };

// Weather (WMO code map + note) for lib/weather.ts.
export const WMO: Record<number, { label: string; fa: string; color: string }> = {
  0: { label: "Clear", fa: "fa-solid fa-sun", color: "#f2c14e" }, 1: { label: "Mainly clear", fa: "fa-solid fa-sun", color: "#f2c14e" },
  2: { label: "Partly cloudy", fa: "fa-solid fa-cloud-sun", color: "#f2c14e" }, 3: { label: "Overcast", fa: "fa-solid fa-cloud", color: "#6c85b8" },
  45: { label: "Fog", fa: "fa-solid fa-smog", color: "#6c85b8" }, 48: { label: "Fog", fa: "fa-solid fa-smog", color: "#6c85b8" },
  51: { label: "Light drizzle", fa: "fa-solid fa-cloud-rain", color: "#49b8ea" }, 53: { label: "Drizzle", fa: "fa-solid fa-cloud-rain", color: "#49b8ea" }, 55: { label: "Drizzle", fa: "fa-solid fa-cloud-rain", color: "#49b8ea" },
  61: { label: "Light rain", fa: "fa-solid fa-cloud-rain", color: "#49b8ea" }, 63: { label: "Rain", fa: "fa-solid fa-cloud-showers-heavy", color: "#49b8ea" }, 65: { label: "Heavy rain", fa: "fa-solid fa-cloud-showers-heavy", color: "#49b8ea" },
  71: { label: "Light snow", fa: "fa-solid fa-snowflake", color: "#c5d7f2" }, 73: { label: "Snow", fa: "fa-solid fa-snowflake", color: "#c5d7f2" }, 75: { label: "Heavy snow", fa: "fa-solid fa-snowflake", color: "#c5d7f2" },
  80: { label: "Rain showers", fa: "fa-solid fa-cloud-showers-heavy", color: "#49b8ea" }, 81: { label: "Rain showers", fa: "fa-solid fa-cloud-showers-heavy", color: "#49b8ea" }, 82: { label: "Heavy showers", fa: "fa-solid fa-cloud-showers-heavy", color: "#49b8ea" },
  95: { label: "Thunderstorms", fa: "fa-solid fa-cloud-bolt", color: "#f2c14e" }, 96: { label: "Thunderstorms", fa: "fa-solid fa-cloud-bolt", color: "#f2c14e" }, 99: { label: "Thunderstorms", fa: "fa-solid fa-cloud-bolt", color: "#f2c14e" },
};
export function weatherNote(code: number, t: number): string {
  if (code >= 95) return "Storms around. Outdoor dailies are free to skip.";
  if (code >= 61 && code <= 82) return "Rain about. Outdoor dailies are free to skip.";
  if (code >= 71 && code <= 77) return "Snow out there. Bundle up or move it indoors.";
  if (t >= 33) return "Hot one. Hydrate and take the outdoor stuff easy.";
  if (t <= 5) return "Cold out. Layer up before anything outside.";
  return "Clear enough, a good day to get outside.";
}
export const DEFAULT_LOC = { lat: 14.1153, lon: 120.9621, place: "Tagaytay" };

export const CUR_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const curMonth = (): string => CUR_MONTHS[new Date().getMonth()] as string;
// Ledger dates retain their year so historical date filters stay stable.
export const curDate = (): string => {
  const d = new Date();
  return `${CUR_MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

// Today as the schema's YYYY-MM-DD (what quest date fields, created/updated/due/
// done_at, store). Local time so it matches the user's day, not UTC.
export const isoToday = (): string => {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
};

// Capital Case for display labels, e.g. "side" → "Side", "at risk" → "At Risk".
// Display only. The underlying schema values (quest_type, etc.) stay lowercase.
export const titleCase = (s: string): string => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

// Friendly rendering of a YYYY-MM-DD date field, e.g. "6 Aug 2026". Blank in → "None".
export const fmtDate = (iso: string | undefined): string => {
  if (!iso) return "None";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const mon = CUR_MONTHS[Number(m[2]) - 1] || m[2];
  return Number(m[3]) + " " + mon + " " + m[1];
};

// "15 Nov": a step's date, day first like fmtDate, without the year.
export const fmtDay = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return Number(m[3]) + " " + (CUR_MONTHS[Number(m[2]) - 1] || m[2]);
};
